import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../../../../components/layout/DashboardLayout';
import EmisorTab from '../tabs/EmisorTab';
import SecuenciasTab from '../tabs/SecuenciasTab';
import ServiciosTab from '../tabs/ServiciosTab';
import CertificacionTab from '../tabs/CertificacionTab';
import { ecfService, type EcfConfig } from '../../../../services/ecf.service';

/**
 * Configuración e-CF: certificado digital, secuencias, servicios web y certificación ante la DGII.
 *
 * La pestaña activa va en la URL (`?tab=`) y no en estado local, que es lo habitual en el resto
 * del proyecto. Es a propósito: el panel y el asistente de certificación enlazan a pestañas
 * concretas (`?tab=secuencias`), y con estado local esos enlaces no podrían funcionar.
 *
 * Las cuatro pestañas leen del backend real: no queda ningún dato de ejemplo en el módulo. Lo
 * único que no viene de la base son los catálogos fijos de la DGII, en `catalogos.ts`.
 */

const TABS = [
  { id: 'emisor', label: 'Emisor y certificado', icon: 'ri-shield-check-line' },
  { id: 'secuencias', label: 'Secuencias e-NCF', icon: 'ri-list-ordered' },
  { id: 'servicios', label: 'Servicios web', icon: 'ri-server-line' },
  { id: 'certificacion', label: 'Certificación', icon: 'ri-building-line' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export default function EcfConfiguracionPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [config, setConfig] = useState<EcfConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const tabParam = searchParams.get('tab');
  const tab: TabId = TABS.some((t) => t.id === tabParam) ? (tabParam as TabId) : 'emisor';

  const setTab = (id: TabId) => setSearchParams({ tab: id }, { replace: true });

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setConfig(await ecfService.getConfig());
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'No se pudo cargar la configuración');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Cambios en memoria mientras se edita el formulario; el guardado lo dispara cada pestaña. */
  const handleChange = (patch: Partial<EcfConfig>) =>
    setConfig((prev) => (prev ? { ...prev, ...patch } : prev));

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Encabezado */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configuración e-CF</h1>
            <p className="text-gray-600">
              Certificado digital, secuencias, servicios web y certificación ante la DGII
            </p>
          </div>
          <button
            onClick={() => navigate('/taxes/ecf')}
            className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Volver
          </button>
        </div>

        {/* Pestañas */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="border-b border-gray-200 px-6">
            <nav className="-mb-px flex gap-6 overflow-x-auto">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    tab === t.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <i className={`${t.icon} mr-2`}></i>
                  {t.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {!loading && loadError && (
          <div className="bg-red-50 text-red-700 border border-red-200 p-4 rounded-lg flex items-center justify-between gap-4">
            <span>
              <i className="ri-error-warning-line mr-2"></i>
              {loadError}
            </span>
            <button
              onClick={load}
              className="text-sm font-medium hover:underline whitespace-nowrap"
            >
              Reintentar
            </button>
          </div>
        )}

        {!loading && !loadError && config && (
          <>
            {tab === 'emisor' && (
              <EmisorTab config={config} onChange={handleChange} onReload={load} />
            )}
            {tab === 'secuencias' && <SecuenciasTab environment={config.environment} />}
            {tab === 'servicios' && <ServiciosTab config={config} />}
            {tab === 'certificacion' && (
              <CertificacionTab config={config} onChange={handleChange} />
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
